import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { callPostApi } from "../../api/ApiCaller";
import { SET_GAME_BONUS_RESULT_POST } from "../../api/ApiConst";
import { TV_SND, playTv, chainTv, stopTv, isPlayingTv } from "./tvSound";

// TV Millionario's JACKPOT GAME bonus, as played in
// https://www.youtube.com/watch?v=qzZSNKlRcCg (0:53-1:51). Drawn on the same
// 800x600 stage as the reels, inside .tvm-stage. Every position below was
// found by registering the video onto the stage (stage x = (vx-37)/1.02,
// stage y = (vy-48)/0.64 at 854x480) and template-matching the sprites in
// public/tv-milionario/bonus/jackpot-game-bonus; every duration was counted
// off the video's frames.
//
// What happens: the reels are covered by two chevron panels, the "TV SHOW
// JACKPOT GAME" title plays, the panels open on a 4x4 board of CASH cards.
// A pink selector walks the cards; INICIAR (or a click on a card) turns one
// over. CASH -> a value that goes into GANA, RENEWAL -> every card is dealt
// again, EXIT -> the round is over and GANA is paid.
//
// The server decides the amount (getBonusResult). The cards only tell that
// amount as a story: it is split into a few CASH values, maybe a RENEWAL or
// two, then EXIT — so GANA always ends on exactly what is paid.

const JG = `${process.env.PUBLIC_URL}/tv-milionario/bonus/jackpot-game-bonus`;
const REF = `${JG}/refactored`;
const FNT = `${process.env.PUBLIC_URL}/tv-milionario/footer-buttons`;

const pad2 = (n) => String(n).padStart(2, "0");
const seqSrc = (dir, f, padded = true) => `${REF}/${dir}/sprite_${padded ? pad2(f) : f}.png`;

// ---- intro (ms per frame, counted off the video at 29.97fps) ----
// presentation_scene_2: the chevron panels closing over the reels; played
// backwards they open onto the board.
const CLOSE_MS = [133, 100, 133, 100, 67];
const OPEN_MS = [100, 67, 133, 100, 133]; // frames 4..0
// presentation_scene_1: city, starburst, JACKPOT GAME zooms in and settles,
// starburst flies off. Holds longest on 12 (the settled title).
const TITLE_MS = [133, 100, 133, 133, 67, 100, 133, 133, 100, 100, 133, 67, 267, 100, 33, 67, 133, 133, 133];
const sum = (a) => a.reduce((s, v) => s + v, 0);
const frameAt = (list, t) => {
    let acc = 0;
    for (let i = 0; i < list.length; i++) { acc += list[i]; if (t < acc) return i; }
    return list.length - 1;
};

// ---- board ----
// card lefts/tops (template-matched), and which file holds each position's
// perspective (2 frames side by side: bulbs off, bulbs lit)
const CARD_L = [29, 164, 298, 433];
const CARD_T = [96, 194, 290, 386];
const CARD_FILE = [
    "card_1", "card_2+3", "card_2+3", "card_4",
    "card_5+9", "card_6+7+10+11", "card_6+7+10+11", "card_8+12",
    "card_5+9", "card_6+7+10+11", "card_6+7+10+11", "card_8+12",
    "card_13", "card_14+15", "card_14+15", "card_16",
];
const CARD_W = [132, 131, 131, 134, 132, 131, 131, 134, 132, 131, 131, 134, 132, 131, 131, 134];
const CARD_H = [94, 94, 94, 94, 92, 92, 92, 92, 92, 92, 92, 92, 96, 96, 96, 96];
const cardSrc = (i) => `${JG}/cards/${encodeURIComponent(CARD_FILE[i])}.png`;
const center = (i) => ({ x: CARD_L[i % 4] + CARD_W[i] / 2, y: CARD_T[Math.floor(i / 4)] + CARD_H[i] / 2 });

const CLICK_FRAME_MS = 80;   // card_click: 5 frames, the card flashes white twice
const FLIP_FRAME_MS = 42;    // card_win / card_exit / card_restart: 12 frames
const CLICK_MS = 5 * CLICK_FRAME_MS;
const FLIP_MS = 12 * FLIP_FRAME_MS;
const VALUE_BLINK_MS = 1500; // the value blinks this long before it goes into GANA
const GANA_RED_MS = 700;     // GANA shows red digits just after it changes
const RENEW_MS = 2200;       // RENEWAL banner up, then every card is dealt again
const EXIT_MS = 3000;        // the hostess shrugs, SALIR, back to the reels
const STEP_MS = 730;         // the selector moves on one card
const COUNTDOWN = 10;        // SELECCIONAR UNO 10..0, then the selected card is taken
const BULB_FLASH_MS = 500;   // every card's bulbs light as a new round starts
const WOMAN_FRAME_MS = 66;   // the hostess clips run at 15fps (man/animInit.anim)

// hostess: idle at (604,56); win and sorry are wider sprites of the same pose,
// shifted so the figure stays put
const WOMAN = {
    idle: { dir: "woman_idle", frames: 15, left: 604, loop: true },
    win: { dir: "woman_win", frames: 36, left: 604 - 112 },
    sorry: { dir: "woman_sorry", frames: 46, left: 604 - 57 },
};

// sprite fonts: [file, glyph w, glyph h]
const FONT = {
    prizeOn: [`${JG}/number_prize_on.png`, 24, 32],
    prizeOff: [`${JG}/number_prize_off.png`, 24, 32],
    ganaBlue: [`${JG}/number_gana_blue.png`, 19, 22],
    ganaRed: [`${JG}/number_gana_red.png`, 21, 24],
    gmm: [`${JG}/number_gmm.png`, 14, 14],
    blue: [`${FNT}/fnt_blue.png`, 14, 18],
    white: [`${FNT}/fnt_white.png`, 16, 18],
};

const Num = ({ value, font, scale = 1, style }) => {
    const [src, w, h] = FONT[font];
    const text = String(Math.max(0, Math.round(Number(value) || 0)));
    return (
        <span style={{ display: "inline-flex", ...style }}>
            {text.split("").map((d, i) => (
                <span key={i} style={{
                    width: w * scale,
                    height: h * scale,
                    backgroundImage: `url(${src})`,
                    backgroundSize: `${w * 10 * scale}px ${h * scale}px`,
                    backgroundPosition: `${-Number(d) * w * scale}px 0`,
                }} />
            ))}
        </span>
    );
};

const rand = (n) => Math.floor(Math.random() * n);

// The server's amount, told as cards: 3-7 CASH values (round numbers when the
// amount allows), 0-2 RENEWALs among them, then EXIT. Nothing -> EXIT at once.
export const buildPicks = (total) => {
    const amount = Number(total) || 0;
    if (amount <= 0) return [{ kind: "exit" }];
    const whole = Math.floor(amount);
    const cents = Math.round((amount - whole) * 100) / 100;
    let values;
    if (whole === 0) {
        values = [amount];
    } else {
        let n = 3 + rand(5);
        let unit = [1000, 500, 100, 50, 10, 5, 1].find((u) => whole % u === 0 && whole / u >= n);
        if (!unit) { unit = 1; n = Math.min(n, whole); }
        const units = whole / unit;
        const weights = Array.from({ length: n }, () => 0.3 + Math.random());
        const wsum = sum(weights);
        const parts = weights.map((w) => 1 + Math.floor((units - n) * w / wsum));
        let left = units - sum(parts);
        while (left-- > 0) parts[rand(n)] += 1;
        values = parts.map((p) => p * unit);
        values[values.length - 1] += cents;
    }
    const picks = values.map((v) => ({ kind: "cash", amount: v }));
    for (let r = rand(3); r > 0; r--) picks.splice(rand(picks.length), 0, { kind: "restart" });
    picks.push({ kind: "exit" });
    return picks;
};

const freshCards = () => Array.from({ length: 16 }, () => ({ kind: "cash", anim: "idle", at: 0, amount: 0 }));

const MSG = {
    select: "msg_select_one",
    good_lucky: "msg_good_lucky",
    win: "msg_win",
    renew: "msg_renew",
    exit: "msg_exit",
};

const JackpotGameBonus = ({
    gameResult, gameSessionRecordId, keyboardInput,
    credits, totalBet, accumulados, onBonusRoundCompleted, testPayout,
}) => {
    const startedAt = useRef(Date.now());
    const [now, setNow] = useState(Date.now());
    const [phase, setPhase] = useState("close"); // close | title | open | board
    const [cards, setCards] = useState(freshCards);
    const [selector, setSelector] = useState(0);
    const [canPick, setCanPick] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN);
    const [gana, setGana] = useState(0);
    const [ganaChangedAt, setGanaChangedAt] = useState(-1e9);
    const [woman, setWoman] = useState({ clip: "idle", at: 0 });
    const [message, setMessage] = useState("select");
    const [renewal, setRenewal] = useState(false);
    const [bulbsAt, setBulbsAt] = useState(-1e9);

    const alive = useRef(true);
    const picks = useRef(null);       // the story, from the server's amount
    const payout = useRef(0);         // what onBonusRoundCompleted is handed
    const step = useRef(0);
    const busy = useRef(false);
    const boardReady = useRef(false);
    const stateRef = useRef({});
    stateRef.current = { cards, selector, canPick };
    const onDone = useRef(onBonusRoundCompleted);
    onDone.current = onBonusRoundCompleted;

    const wait = (ms) => new Promise((res) => setTimeout(res, ms));

    // the machine's music: TvMillionarioGame starts it as the JACKPOTs land
    // (started here only for the dev round); its bed runs on under the
    // board and through EXIT, and stops as the reels come back
    useEffect(() => {
        if (!isPlayingTv(TV_SND.jackpotIntro, TV_SND.jackpotLoop)) chainTv(TV_SND.jackpotIntro, TV_SND.jackpotLoop);
        return () => stopTv(TV_SND.jackpotIntro, TV_SND.jackpotLoop);
    }, []);

    // ---- clock + preload + result ----
    useEffect(() => {
        alive.current = true;
        const id = setInterval(() => setNow(Date.now()), 33);
        const srcs = [];
        for (let f = 0; f < 5; f++) srcs.push(seqSrc("presentation_scene_2", f, false));
        for (let f = 0; f < 19; f++) srcs.push(seqSrc("presentation_scene_1", f));
        for (let f = 0; f < 4; f++) srcs.push(seqSrc("background_bottom", f, false));
        for (let f = 0; f < 11; f++) srcs.push(seqSrc("light_cannon", f));
        for (let f = 0; f < 5; f++) srcs.push(seqSrc("card_click", f, false));
        for (let f = 0; f < 12; f++) ["card_win", "card_exit", "card_restart"].forEach((d) => srcs.push(seqSrc(d, f)));
        Object.values(WOMAN).forEach((w) => { for (let f = 0; f < w.frames; f++) srcs.push(seqSrc(w.dir, f)); });
        CARD_FILE.forEach((_, i) => srcs.push(cardSrc(i)));
        srcs.push(`${JG}/background_top.png`, `${JG}/restart.png`, `${JG}/card_selector.png`, `${REF}/drape/sprite_00.png`);
        srcs.forEach((s) => { const im = new Image(); im.src = s; });

        // dev test round (TvMillionarioGame's "k" key): no server call
        if (testPayout !== undefined) {
            setTimeout(() => {
                if (!alive.current) return;
                payout.current = testPayout;
                picks.current = buildPicks(testPayout);
                if (boardReady.current) startRound(0);
            }, 50);
            return () => { alive.current = false; clearInterval(id); };
        }

        callPostApi(SET_GAME_BONUS_RESULT_POST,{ bonusType: gameResult?.bonusType, recordId: gameSessionRecordId }, {}, (response) => {
            if (!alive.current) return;
            if (response?.errorCode === 0) {
                const res = response.responsePacket || {};
                payout.current = Number(res.totalWinnings ?? res.totalBonusWinnings ?? 0) || 0;
                if (process.env.NODE_ENV === "development") {
                    // 0 here means the round goes straight to EXIT and no YOU WIN follows
                    console.info("[JACKPOT GAME] getBonusResult pays", payout.current, res);
                }
            } else {
                toast.error(response?.message || "Bonus result not loaded");
                payout.current = 0;
            }
            picks.current = buildPicks(payout.current);
            if (boardReady.current) startRound(0);
        }, (error) => {
            if (!alive.current) return;
            toast.error(typeof error === "string" ? error : "Bonus result not loaded");
            payout.current = 0;
            picks.current = buildPicks(0);
            if (boardReady.current) startRound(0);
        });
        return () => { alive.current = false; clearInterval(id); };
    }, []);

    // ---- intro ----
    const introT = now - startedAt.current;
    const closeEnd = sum(CLOSE_MS);
    const titleEnd = closeEnd + sum(TITLE_MS);
    const openEnd = titleEnd + sum(OPEN_MS);
    useEffect(() => {
        if (phase === "close" && introT >= closeEnd) setPhase("title");
        else if (phase === "title" && introT >= titleEnd) setPhase("open");
        else if (phase === "open" && introT >= openEnd) {
            setPhase("board");
            boardReady.current = true;
            if (picks.current) startRound(0);
        }
    }, [now]);

    // ---- picking ----
    const nextFree = (from, list) => {
        for (let k = 0; k < 16; k++) {
            const i = (from + k) % 16;
            if (list[i].anim === "idle") return i;
        }
        return from;
    };

    const startRound = (from) => {
        if (!alive.current) return;
        setSelector(nextFree(from, stateRef.current.cards));
        setCountdown(COUNTDOWN);
        setMessage("select");
        setCanPick(true);
        busy.current = false;
    };

    // the selector walks while a card can be taken
    useEffect(() => {
        if (!canPick) return;
        playTv(TV_SND.selOpen);
        const id = setInterval(() => {
            setSelector((s) => nextFree(s + 1, stateRef.current.cards));
        }, STEP_MS);
        return () => clearInterval(id);
    }, [canPick]);

    // SELECCIONAR UNO 10..0: at 0 the selected card is taken
    useEffect(() => {
        if (!canPick) return;
        const id = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(id);
    }, [canPick]);
    useEffect(() => {
        if (canPick && countdown < COUNTDOWN && countdown > 0) playTv(TV_SND.selTick);
        if (canPick && countdown === 0) pick(stateRef.current.selector);
    }, [countdown, canPick]);

    const setCard = (i, patch) => setCards((list) => list.map((c, k) => (k === i ? { ...c, ...patch } : c)));

    const pick = async (i) => {
        if (busy.current || !stateRef.current.canPick || !picks.current) return;
        if (stateRef.current.cards[i]?.anim !== "idle") return;
        busy.current = true;
        setCanPick(false);
        setSelector(i);
        setMessage("good_lucky");
        playTv(TV_SND.selPick);

        setCard(i, { anim: "click", at: Date.now() });
        await wait(CLICK_MS);
        if (!alive.current) return;

        const outcome = picks.current[Math.min(step.current, picks.current.length - 1)];
        step.current += 1;
        setCard(i, { kind: outcome.kind, amount: outcome.amount || 0, anim: "flip", at: Date.now() });

        if (outcome.kind === "cash") {
            await wait(FLIP_MS);
            if (!alive.current) return;
            setCard(i, { anim: "blink", at: Date.now() });
            setMessage("win");
            setWoman({ clip: "win", at: Date.now() });
            playTv(TV_SND.creditIn);
            await wait(VALUE_BLINK_MS);
            if (!alive.current) return;
            setCard(i, { anim: "done" });
            playTv(TV_SND.creditTv);
            setGana((g) => g + outcome.amount);
            setGanaChangedAt(Date.now());
            startRound(i + 1);
        } else if (outcome.kind === "restart") {
            setRenewal(true);
            setMessage("renew");
            playTv(TV_SND.selectBeep);
            await wait(RENEW_MS);
            if (!alive.current) return;
            setRenewal(false);
            setCards(freshCards());
            stateRef.current.cards = freshCards();
            setBulbsAt(Date.now());
            startRound(i + 1);
        } else {
            await wait(FLIP_MS);
            if (!alive.current) return;
            setCard(i, { anim: "done" });
            setMessage("exit");
            setWoman({ clip: "sorry", at: Date.now() });
            // GANA ends on exactly the paid amount, whatever rounding the
            // split did on the way
            setGana(payout.current);
            await wait(EXIT_MS);
            if (!alive.current) return;
            onDone.current(payout.current);
        }
    };

    // INICIAR takes the selected card (same "b" key as the reels)
    useEffect(() => {
        if (!keyboardInput) return;
        const onKey = (e) => {
            const k = e.key.toLowerCase();
            if (k === "b" || k === "enter") pick(stateRef.current.selector);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [keyboardInput]);

    // ---- rendering ----
    const t = now;
    const abs = (style) => ({ position: "absolute", display: "block", pointerEvents: "none", ...style });

    if (phase === "close" || phase === "title") {
        return (
            <div className="jgb-layer">
                {phase === "close"
                    ? <img alt="" style={abs({ left: 0, top: 0, width: 800, height: 600 })}
                        src={seqSrc("presentation_scene_2", frameAt(CLOSE_MS, introT), false)} />
                    : <img alt="" style={abs({ left: 0, top: 0, width: 800, height: 600 })}
                        src={seqSrc("presentation_scene_1", frameAt(TITLE_MS, introT - closeEnd))} />}
            </div>
        );
    }

    const bottomFrame = Math.floor(t / 150) % 4;
    const womanClip = WOMAN[woman.clip];
    let womanFrame = Math.floor((t - woman.at) / WOMAN_FRAME_MS);
    let clip = womanClip;
    if (!womanClip.loop && womanFrame >= womanClip.frames) {
        clip = woman.clip === "sorry" ? womanClip : WOMAN.idle;
        womanFrame = woman.clip === "sorry" ? womanClip.frames - 1 : womanFrame;
    }
    if (clip.loop) womanFrame = Math.floor(t / WOMAN_FRAME_MS) % clip.frames;
    const ganaRed = t - ganaChangedAt < GANA_RED_MS;
    const bulbsLit = t - bulbsAt < BULB_FLASH_MS;

    const renderCard = (c, i) => {
        const { x, y } = center(i);
        const fx = (w = 125, h = 88) => ({ left: x - w / 2, top: y - h / 2, width: w, height: h });
        const els = [];
        const elapsed = t - c.at;
        if (c.anim === "idle") {
            els.push(<div key="c" style={abs({
                left: CARD_L[i % 4], top: CARD_T[Math.floor(i / 4)], width: CARD_W[i], height: CARD_H[i],
                backgroundImage: `url(${cardSrc(i)})`,
                backgroundPosition: `${bulbsLit ? -CARD_W[i] : 0}px 0`,
            })} />);
            if (canPick && selector === i) {
                els.push(<img key="s" alt="" src={`${JG}/card_selector.png`} style={abs(fx())} />);
            }
        } else if (c.anim === "click") {
            const f = Math.min(4, Math.floor(elapsed / CLICK_FRAME_MS));
            els.push(<img key="c" alt="" src={seqSrc("card_click", f, false)} style={abs(fx(128, 91))} />);
        } else {
            const dir = { cash: "card_win", exit: "card_exit", restart: "card_restart" }[c.kind];
            let f = 11;
            if (c.anim === "flip") f = Math.min(11, Math.floor(elapsed / FLIP_FRAME_MS));
            // a revealed value: bright blue while it blinks, dark once banked
            if (c.kind === "cash" && c.anim !== "flip") f = c.anim === "blink" ? 10 : 11;
            els.push(<img key="c" alt="" src={seqSrc(dir, f)} style={abs(fx())} />);
            if (c.kind === "cash" && c.anim !== "flip") {
                const len = String(Math.round(c.amount)).length;
                const scale = Math.min(0.85, 100 / (len * 24));
                els.push(
                    <div key="n" style={abs({ ...fx(), display: "flex", alignItems: "center", justifyContent: "center" })}>
                        <Num value={c.amount} font={c.anim === "blink" ? "prizeOn" : "prizeOff"} scale={scale} />
                    </div>
                );
                if (c.anim === "blink" && Math.floor(elapsed / 100) % 2 === 0) {
                    els.push(<img key="p" alt="" src={`${JG}/JP_PISCA_CEL_EXIT.png`} style={abs(fx(130, 91))} />);
                }
            }
        }
        if (c.anim === "idle" && canPick) {
            els.push(<div key="h" className="jgb-hit" onClick={() => pick(i)}
                style={{ left: CARD_L[i % 4], top: CARD_T[Math.floor(i / 4)], width: CARD_W[i], height: CARD_H[i] }} />);
        }
        return <React.Fragment key={i}>{els}</React.Fragment>;
    };

    return (
        <div className="jgb-layer">
            {/* scenery */}
            <img alt="" src={`${JG}/background_top.png`} style={abs({ left: 0, top: 0, width: 800, height: 315 })} />
            <img alt="" src={seqSrc("light_cannon", Math.floor(t / 100) % 11)} style={abs({ left: 1, top: 61, width: 198, height: 126 })} />
            <img alt="" src={seqSrc("spot_lights_vert", Math.floor(t / 150) % 4, false)} style={abs({ left: 236, top: 38, width: 76, height: 124 })} />
            <img alt="" src={seqSrc("spot_lights_hor", Math.floor(t / 150) % 4, false)} style={abs({ left: 313, top: 38, width: 104, height: 30 })} />
            <img alt="" src={`${REF}/drape/sprite_00.png`} style={abs({ left: 568, top: 30, width: 166 * 1.4, height: 256 * 1.4 })} />
            <img alt="" src={seqSrc("background_bottom", bottomFrame, false)} style={abs({ left: 0, top: 315, width: 800, height: 285 })} />

            {/* hostess, then the footer panel again over her feet */}
            <img alt="" src={seqSrc(clip.dir, womanFrame)} style={abs({ left: clip.left, top: 56 })} />
            {woman.clip === "win" && clip === WOMAN.win && womanFrame >= 3 && (
                // the sprite was cut from a screen that showed MAJOR 80000:
                // drop its header strip so our own box and value show
                <img alt="" src={`${REF}/balloons/sprite_0.png`}
                    style={abs({ left: 457, top: 2, width: 169, height: 123, clipPath: "inset(30px 0 0 0)" })} />
            )}
            <div style={abs({
                left: 0, top: 492, width: 800, height: 108,
                backgroundImage: `url(${seqSrc("background_bottom", bottomFrame, false)})`,
                backgroundPosition: "0 -177px",
            })} />

            {cards.map(renderCard)}

            {renewal && <img alt="" src={`${JG}/restart.png`} style={abs({ left: 92, top: 244, width: 615, height: 112 })} />}

            {/* header: GANA, GRAND / MAJOR / MINOR */}
            <div style={abs({ left: 80, top: 17, width: 120, display: "flex", justifyContent: "center" })}>
                <Num value={gana} font={ganaRed ? "ganaRed" : "ganaBlue"} scale={0.85} />
            </div>
            {[350, 543, 737].map((cx, k) => (
                <div key={k} style={abs({ left: cx - 60, top: 11, width: 120, display: "flex", justifyContent: "center" })}>
                    <Num value={accumulados?.[k] || 0} font="gmm" scale={0.8} />
                </div>
            ))}

            {/* footer: CRÉDITO, message, AUMENTO, GANA, INICIAR */}
            <div style={abs({ left: 172, top: 499 })}><Num value={credits} font="blue" scale={0.92} /></div>
            <div style={abs({ left: 643, top: 499, width: 100, display: "flex", justifyContent: "flex-end" })}>
                <Num value={totalBet} font="blue" scale={0.92} />
            </div>
            <div style={abs({ left: 643, top: 519, width: 100, display: "flex", justifyContent: "flex-end" })}>
                <Num value={gana} font="blue" scale={0.92} />
            </div>
            <div style={abs({ left: 31, top: 518, width: 560, height: 17, display: "flex", justifyContent: "center" })}>
                <img alt="" src={`${FNT}/${MSG[message]}.png`} />
            </div>
            {message === "select" && canPick && (
                <div className="jgb-countdown" style={abs({ left: 440, top: 519 })}>
                    <Num value={countdown} font="white" scale={0.8} />
                </div>
            )}
            {!canPick && <img alt="" src={`${JG}/button_start.png`} style={abs({ left: 526, top: 541, width: 189, height: 46 })} />}
            <button className="jgb-start" disabled={!canPick} onClick={() => pick(selector)} />

            {phase === "open" && (
                <img alt="" style={abs({ left: 0, top: 0, width: 800, height: 600 })}
                    src={seqSrc("presentation_scene_2", 4 - frameAt(OPEN_MS, introT - titleEnd), false)} />
            )}
        </div>
    );
};

export default JackpotGameBonus;
